"use client"

import Link from "next/link"
import { useEffect, useRef, useState } from "react"
import {
  motion,
  useScroll,
  useTransform,
  useReducedMotion,
  AnimatePresence,
  type MotionValue,
} from "framer-motion"
import { ArrowUpRight, ArrowRight } from "lucide-react"
import { Marquee } from "@/components/ui/marquee"
import {
  FadeUp,
  MagneticBtn,
  ScrollProgress,
  ParallaxY,
} from "@/components/landing/motion-primitives"
import { SiteHeader } from "@/components/landing/site-header"
import { SearchShift } from "@/components/landing/search-shift"
import { KeyFeatures } from "@/components/landing/key-features"
import { ActionsSection } from "@/components/landing/actions-section"
import { FinalCta } from "@/components/landing/final-cta"
import { FooterSection } from "@/components/landing/footer-section"
import { AnswerMonitor } from "@/components/landing/answer-monitor"
import { SURFACES_LABEL } from "@/lib/site-config"
import { fireAnalyticsEvent as track } from "@/lib/analytics"
import "./old-landing/old-landing.css"

/* ─────────────────────────────────────────────────────────────
   PAGE
   ───────────────────────────────────────────────────────────── */
export default function VelovaLanding() {
  return (
    <main className="old-landing velova-canvas velova-grain min-h-screen overflow-x-clip">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[70] focus:px-4 focus:py-2 focus:rounded-full focus:bg-white focus:text-black focus:text-[13px] focus:font-medium"
      >
        Skip to content
      </a>
      <ScrollProgress />
      <SiteHeader />
      <Hero />
      <ModelCoverageRail />
      <SearchShift />
      <Method />
      <KeyFeatures />
      <ActionsSection />
      <FinalCta />
      <FooterSection />
    </main>
  )
}

/* ─────────────────────────────────────────────────────────────
   Local hook: hover-capable device detection (desktop/mouse)
   ───────────────────────────────────────────────────────────── */
function useHasHover() {
  const [has, setHas] = useState(false)
  useEffect(() => {
    if (typeof window === "undefined") return
    const mq = window.matchMedia("(hover: hover) and (pointer: fine)")
    const update = () => setHas(mq.matches)
    update()
    mq.addEventListener("change", update)
    return () => mq.removeEventListener("change", update)
  }, [])
  return has
}

/* ─────────────────────────────────────────────────────────────
   Hero rotating headline word — "Be the brand AI [finds. /
   understands. / trusts. / recommends.]". Fixed 2s visible / 450ms
   crossfade cadence, same duration for every word, seamless loop.
   Width is reserved via a CSS-grid stacked ghost sized to the
   longest word ("understands.") so no swap ever shifts layout.
   Purely decorative (aria-hidden) — the parent H1 carries one
   static sr-only sentence so the heading stays meaningful without
   animation and isn't re-announced on every rotation.
   ───────────────────────────────────────────────────────────── */
const ROTATING_HEADLINE_WORDS = ["finds.", "understands.", "trusts.", "recommends."] as const
const ROTATING_WORD_VISIBLE_MS = 2000
const ROTATING_WORD_TRANSITION_S = 0.45

function RotatingHeadlineWord() {
  const reduce = useReducedMotion()
  const [index, setIndex] = useState(0)

  useEffect(() => {
    if (reduce) return
    const id = setInterval(
      () => setIndex((i) => (i + 1) % ROTATING_HEADLINE_WORDS.length),
      ROTATING_WORD_VISIBLE_MS,
    )
    return () => clearInterval(id)
  }, [reduce])

  const wordClass = "velova-italic hero-word-glow whitespace-nowrap"
  const wordStyle = { color: "var(--velova-blue-deep)" } as const

  if (reduce) {
    return (
      <span className={wordClass} style={wordStyle}>
        recommends.
      </span>
    )
  }

  return (
    <span aria-hidden="true" className="relative inline-grid align-baseline">
      {/* Invisible sizer — reserves box space for the longest word. */}
      <span className={`${wordClass} invisible col-start-1 row-start-1`} style={wordStyle}>
        understands.
      </span>
      {/* mode="popLayout" (same pattern as WordRotate in motion-primitives.tsx)
          pops the exiting word out of flow and reliably unmounts it once its
          exit finishes — without it, exited words were staying in the DOM
          (an accumulating-node leak; caught via manual DOM inspection). */}
      <AnimatePresence mode="popLayout" initial={false}>
        <motion.span
          key={ROTATING_HEADLINE_WORDS[index]}
          className={wordClass}
          style={{ ...wordStyle, gridArea: "1 / 1 / 2 / 2" }}
          initial={{ opacity: 0, y: 10, filter: "blur(6px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          exit={{ opacity: 0, y: -10, filter: "blur(6px)" }}
          transition={{ duration: ROTATING_WORD_TRANSITION_S, ease: [0.22, 1, 0.36, 1] }}
        >
          {ROTATING_HEADLINE_WORDS[index]}
        </motion.span>
      </AnimatePresence>
    </span>
  )
}

/** Smooth, in-page anchor scroll (native click / no modifier keys only —
 * middle-click, cmd/ctrl-click etc. fall through to normal <a> behaviour).
 * Respects prefers-reduced-motion by jumping instead of animating. */
function scrollToId(e: React.MouseEvent<HTMLAnchorElement>, id: string) {
  if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
  const el = document.getElementById(id)
  if (!el) return
  e.preventDefault()
  const reduce =
    typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches
  el.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" })
}

/* ─────────────────────────────────────────────────────────────
   1 — HERO
   Split composition: editorial slate left, product-view monitor right.
   Headline's trailing verb rotates (RotatingHeadlineWord); the monitor
   (AnswerMonitor) runs its own five-phase answer/analysis/diagnosis/
   action loop.
   ───────────────────────────────────────────────────────────── */
function Hero() {
  return (
    <section
      id="main-content"
      className="relative pt-14 md:pt-20 pb-16 md:pb-24 overflow-hidden"
    >
      {/* Aurora background — gentle parallax */}
      <ParallaxY range={50} className="absolute inset-x-0 top-0 h-full pointer-events-none">
        <div className="velova-brush h-full w-full" />
      </ParallaxY>

      <div className="relative velova-container">
        <div className="grid items-center gap-14 lg:gap-10 xl:gap-16 lg:grid-cols-[1.1fr_0.9fr]">
          {/* LEFT — the slate */}
          <div className="text-left">
            {/* Eyebrow */}
            <FadeUp className="flex justify-start" y={12}>
              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 md:px-4 py-1.5 backdrop-blur-md">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--velova-blue-deep)]" />
                <span className="text-[11px] md:text-[12px] tracking-[0.2em] md:tracking-[0.22em] uppercase text-white/70 font-medium">
                  Daily tracking across AI answers
                </span>
              </div>
            </FadeUp>

            {/* Headline — one accessible H1; the visible word rotates on
                its own dedicated line (always, at every width) so the
                layout never depends on whether a given word happens to
                fit inline next to "Be the brand AI" — the sr-only
                sentence stays static for screen readers. */}
            <h1 className="velova-display mt-7 md:mt-9 text-[var(--velova-ink)] tracking-[-0.01em]">
              <span className="sr-only">
                Be the brand AI finds, understands, trusts and recommends.
              </span>
              <FadeUp
                as="span"
                className="block text-[36px] sm:text-[46px] md:text-[54px] lg:text-[50px] xl:text-[56px] 2xl:text-[62px] leading-[1.04]"
                delay={0.1}
                aria-hidden="true"
              >
                Be the brand AI
              </FadeUp>
              <FadeUp
                as="span"
                className="block mt-1 md:mt-2 text-[44px] sm:text-[56px] md:text-[66px] lg:text-[62px] xl:text-[70px] 2xl:text-[78px] leading-[1.02]"
                delay={0.2}
                aria-hidden="true"
              >
                <RotatingHeadlineWord />
              </FadeUp>
            </h1>

            {/* Supporting */}
            <FadeUp delay={0.3} className="mt-7 max-w-[560px]">
              <p className="text-[16px] sm:text-[18px] md:text-[19.5px] leading-[1.55] text-white/65">
                See how your brand appears in AI answers. Find who wins, why
                they win, and what to do next.
              </p>
            </FadeUp>

            {/* Capability labels */}
            <FadeUp delay={0.42} className="mt-6 flex flex-wrap items-center justify-start gap-3 sm:gap-4 text-[11px] sm:text-[13px] tracking-[0.14em] sm:tracking-[0.16em] uppercase text-white/55">
              <span>Daily monitoring</span>
              <span className="text-white/20">·</span>
              <span>Leading AI models</span>
              <span className="text-white/20">·</span>
              <span>Measure, diagnose, act</span>
            </FadeUp>

            {/* CTAs — fixed equal sizing via .hero-cta (old-landing.css) */}
            <FadeUp delay={0.54} className="hero-cta-row mt-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-start gap-3 sm:gap-4">
              <MagneticBtn>
                <Link
                  href="/signup"
                  onClick={() => track("cta_start_free", { page: "home", section: "hero" })}
                  className="velova-btn velova-btn-ink hero-cta hero-cta-ink"
                >
                  Run a free test
                  <ArrowUpRight className="w-[15px] h-[15px] hero-cta-arrow" />
                </Link>
              </MagneticBtn>
              <MagneticBtn strength={8}>
                <Link
                  href="#method"
                  onClick={(e) => {
                    // Same tracked event as the pre-existing hero secondary
                    // CTA (site-config.ts AnalyticsEventName) — the button's
                    // destination/behaviour changed, the event key didn't.
                    track("cta_see_product", { page: "home", section: "hero" })
                    scrollToId(e, "method")
                  }}
                  className="velova-btn velova-btn-ghost hero-cta hero-cta-ghost"
                >
                  See how it works
                  <ArrowRight className="w-[15px] h-[15px] hero-cta-arrow" />
                </Link>
              </MagneticBtn>
            </FadeUp>

            {/* CTA microcopy */}
            <FadeUp delay={0.62} className="mt-3">
              <p className="text-[13px] text-white/45">
                Explore your AI visibility before choosing a plan.
              </p>
            </FadeUp>
          </div>

          {/* RIGHT — the product-view monitor */}
          <div className="relative w-full max-w-[620px] mx-auto lg:mx-0 lg:justify-self-end">
            <AnswerMonitor />
          </div>
        </div>
      </div>
    </section>
  )
}

/* ─────────────────────────────────────────────────────────────
   2 — MODEL COVERAGE RAIL
   Ten AI answer surfaces, shown with official first-party logos
   from /public/img/ai-surfaces (see asset report). Surfaces that
   publish a full lockup (mark + wordmark) render as the logo
   alone; those with no first-party wordmark render as the official
   mark + product name. Coverage per plan lives on /pricing — this
   rail does not imply plan inclusion.
   ───────────────────────────────────────────────────────────── */
const AI_LOGO = "/img/ai-surfaces"

type MarqueeSurface =
  | { kind: "lockup"; name: string; src: string; h: number }
  | { kind: "mark"; name: string; src: string; h: number; square?: boolean }

// Per-logo heights (px, desktop) tuned for equal OPTICAL weight —
// not equal box size — because lockups carry their own wordmark.
const MARQUEE_SURFACES: MarqueeSurface[] = [
  { kind: "lockup", name: "Claude",              src: `${AI_LOGO}/claude.svg`,     h: 30 },
  { kind: "mark",   name: "ChatGPT",             src: `${AI_LOGO}/chatgpt.svg`,    h: 27 },
  { kind: "mark",   name: "Gemini",              src: `${AI_LOGO}/gemini.svg`,     h: 27 },
  { kind: "mark",   name: "Google AI Overviews", src: `${AI_LOGO}/google-g.svg`,   h: 25 },
  { kind: "mark",   name: "Google AI Mode",      src: `${AI_LOGO}/google-g.svg`,   h: 25 },
  { kind: "lockup", name: "Perplexity",          src: `${AI_LOGO}/perplexity.svg`, h: 24 },
  { kind: "mark",   name: "Microsoft Copilot",   src: `${AI_LOGO}/copilot.svg`,    h: 27 },
  { kind: "lockup", name: "Grok",                src: `${AI_LOGO}/grok.svg`,       h: 26 },
  { kind: "mark",   name: "Meta AI",             src: `${AI_LOGO}/meta-ai.png`,    h: 26, square: true },
  { kind: "lockup", name: "DeepSeek",            src: `${AI_LOGO}/deepseek.svg`,   h: 19 },
]

function MarqueeItem({ s }: { s: MarqueeSurface }) {
  const logo = (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={s.src}
      alt={`${s.name} logo`}
      loading="lazy"
      decoding="async"
      draggable={false}
      style={{ height: s.h, width: s.kind === "mark" && s.square ? s.h : "auto" }}
      className="block w-auto select-none pointer-events-none"
    />
  )
  if (s.kind === "lockup") {
    return <div className="flex items-center">{logo}</div>
  }
  return (
    <div className="flex items-center gap-2.5 md:gap-3">
      {logo}
      <span
        className="whitespace-nowrap text-[19px] md:text-[24px] leading-none text-[#e9e6dd]"
        style={{ fontFamily: "var(--font-display), system-ui, sans-serif", fontWeight: 500, letterSpacing: "-0.01em" }}
      >
        {s.name}
      </span>
    </div>
  )
}

function ModelCoverageRail() {
  return (
    <section id="coverage" className="border-y border-[var(--velova-line-strong)] py-8 overflow-hidden bg-[var(--velova-paper-2)] scroll-mt-24">
      <div className="velova-container flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:gap-8 mb-6">
        <span className="velova-mono-label opacity-70 whitespace-normal sm:whitespace-nowrap">{SURFACES_LABEL}</span>
        <div className="hidden sm:block flex-1 h-px bg-[var(--velova-line-strong)]" />
        <Link
          href="/pricing"
          className="velova-mono-label whitespace-nowrap hover:text-white/90 transition-colors inline-flex items-center gap-1.5"
        >
          View plan coverage
          <ArrowRight className="w-3 h-3" />
        </Link>
      </div>

      <div className="velova-fade-x">
        <Marquee pauseOnHover className="[--duration:46s] [--gap:3.25rem]">
          {MARQUEE_SURFACES.map((s, i) => (
            <div key={`${s.name}-${i}`} className="flex items-center">
              <MarqueeItem s={s} />
              <span aria-hidden className="ml-[3.25rem] text-white/[0.14] text-[15px] leading-none">◆</span>
            </div>
          ))}
        </Marquee>
      </div>
    </section>
  )
}

/* ─────────────────────────────────────────────────────────────
   6 — METHOD — the operating loop (scroll-driven stacked cards)
   The only homepage section that teaches the full loop.
   ───────────────────────────────────────────────────────────── */
function Method() {
  const ref = useRef<HTMLDivElement>(null)
  const reduce = useReducedMotion()
  // The stacked-card shuffle now runs on phones too, but the fan-out offsets
  // are authored in pixels against a ~674px desktop column. Reused as-is on a
  // 346px one they throw the deep cards clean off screen, so the transform
  // ranges are picked per viewport instead.
  const [isNarrow, setIsNarrow] = useState(false)
  useEffect(() => {
    // Not just phones: at the md breakpoint the card column is only ~385px,
    // so the desktop +106px fan-out pushes the deepest card past the viewport
    // edge. The gentler ranges run until the column is wide enough (lg).
    const mq = window.matchMedia("(max-width: 1023px)")
    const sync = () => setIsNarrow(mq.matches)
    sync()
    mq.addEventListener("change", sync)
    return () => mq.removeEventListener("change", sync)
  }, [])
  // The reduced-motion branch never attaches
  // `ref` and never reads scrollYProgress, so skip the ref target when
  // reduced rather than crashing on an unhydrated useScroll target.
  const { scrollYProgress } = useScroll(
    reduce ? undefined : { target: ref, offset: ["start start", "end end"] },
  )

  const steps = [
    {
      num: "I",
      title: "MEASURE",
      tagline: "See exactly how AI sees your brand.",
      body:
        "Track your presence, position, sentiment, citations and competitors across the buyer questions that matter.",
      footer: "Daily runs · Citations collected with each run",
      gradient: "linear-gradient(140deg, #001B33 0%, #0050FF 55%, #00D6FF 100%)",
      glow: "rgba(0, 214, 255, 0.38)",
    },
    {
      num: "II",
      title: "DIAGNOSE",
      tagline: "Understand why you win—or disappear.",
      body:
        "Reveal the recurring content, source and positioning patterns shaping how AI presents your brand.",
      footer: "Answer-level evidence · Competitive source gaps",
      gradient: "linear-gradient(140deg, #3A0E2F 0%, #8B2E66 50%, #FCCBFF 100%)",
      glow: "rgba(252, 203, 255, 0.38)",
    },
    {
      num: "III",
      title: "ACT",
      tagline: "Know what to improve next.",
      body:
        "Get prioritised, evidence-backed actions for your website, content and external visibility—ready for your team to review and execute.",
      footer: "Generate · Review · Publish",
      gradient: "linear-gradient(140deg, #0B3F30 0%, #1F9E73 55%, #9CFCE0 100%)",
      glow: "rgba(156, 252, 224, 0.4)",
    },
    {
      num: "IV",
      title: "RE-MEASURE",
      tagline: "See what changed.",
      body:
        "Rerun the affected questions, compare results over time and keep improving with fresh evidence.",
      footer: "Re-measure · Attribute · Repeat",
      gradient: "linear-gradient(140deg, #4E3A08 0%, #C49A2E 55%, #F0EDC0 100%)",
      glow: "rgba(240, 237, 192, 0.4)",
    },
  ]

  const total = steps.length
  const activeIndex = useTransform(
    scrollYProgress,
    [0, 0.1, 0.25, 0.37, 0.52, 0.64, 0.79, 0.92, 1],
    [0, 0, 1, 1, 2, 2, 3, 3, 3],
  )

  /* Reduced-motion fallback: static 2×2 grid, no scroll-hijack. */
  if (reduce) {
    return (
      <section id="method" className="py-24 md:py-32 bg-[var(--velova-paper-2)] border-y border-[var(--velova-line-strong)] scroll-mt-24">
        <div className="velova-container">
          <div className="flex items-center gap-4 mb-8">
            <span className="velova-mono-label opacity-60">The Velova loop</span>
            <div className="flex-1 h-px bg-[var(--velova-line-strong)]" />
          </div>
          <h2 className="velova-display text-[32px] md:text-[48px] leading-[1.05] mb-6 max-w-4xl">
            From every AI answer to the next best move.{" "}
            <span className="velova-italic opacity-70">Then measure what changed.</span>
          </h2>
          <p className="mb-12 max-w-[560px] text-[16px] leading-[1.6] text-white/65">
            Velova tracks how your brand appears, identifies what is shaping
            the result, turns the evidence into prioritised improvements, and
            reruns the questions to see how your position evolves.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {steps.map((s, i) => (
              <div
                key={s.num}
                className="rounded-[20px] p-8 md:p-10 text-white relative overflow-hidden"
                style={{ background: s.gradient, boxShadow: `0 20px 40px -20px ${s.glow}` }}
              >
                <h3 className="velova-display text-[44px] leading-[0.95]">{s.title}</h3>
                <p className="mt-3 text-[18px] opacity-90">{s.tagline}</p>
                <p className="mt-5 text-[14.5px] leading-[1.55] opacity-85">{s.body}</p>
                <div className="mt-5 pt-4 border-t border-white/15 velova-mono-label opacity-80">{s.footer}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    )
  }

  return (
    <section
      id="method"
      ref={ref}
      data-pin-progress
      className="relative bg-[var(--velova-paper-2)] border-y border-[var(--velova-line-strong)] h-[300vh] scroll-mt-24"
    >
      <div className="sticky top-0 h-screen flex items-center overflow-hidden">
        <div className="velova-container w-full">
          <div className="grid grid-cols-12 gap-4 md:gap-10 items-center">
            {/* LEFT — editorial heading */}
            <div className="col-span-12 md:col-span-5">
              <div className="flex items-center gap-4 mb-8">
                <span className="velova-mono-label opacity-60">The Velova loop</span>
                <div className="flex-1 h-px bg-[var(--velova-line-strong)]" />
              </div>

              <h2 className="velova-display text-[26px] sm:text-[30px] md:text-[40px] lg:text-[50px] leading-[1.04]">
                From every AI answer to the next best move.<br />
                <span className="velova-italic opacity-70">Then measure what changed.</span>
              </h2>

              <p className="mt-5 md:mt-8 max-w-[460px] text-[14.5px] sm:text-[16px] leading-[1.55] md:leading-[1.6] text-white/65">
                Velova tracks how your brand appears, identifies what is
                shaping the result, turns the evidence into prioritised
                improvements, and reruns the questions to see how your
                position evolves.
              </p>

              <div className="mt-8 md:mt-12 flex items-end gap-3 lg:gap-4">
                {steps.map((s, i) => (
                  <MethodDot key={s.num} index={i} activeIndex={activeIndex} label={s.title} />
                ))}
              </div>

              <div className="mt-8 hidden md:flex items-center gap-3 text-[11px] uppercase tracking-[0.22em] text-white/45">
                <span className="inline-block w-4 h-px bg-white/40" />
                Scroll to advance
                <a href="#platform" className="ml-4 underline-offset-4 hover:text-white/80 transition-colors">
                  Skip →
                </a>
              </div>
            </div>

            {/* Desktop — stacked gradient cards with scroll-driven shuffle */}
            <div
              className="col-span-12 md:col-span-7 relative h-[300px] sm:h-[380px] md:h-[520px] mt-6 md:mt-0"
              style={{ perspective: "1800px" }}
            >
              {steps.map((step, i) => (
                <MethodCard
                  key={step.num}
                  index={i}
                  total={total}
                  activeIndex={activeIndex}
                  step={step}
                  narrow={isNarrow}
                />
              ))}
            </div>

          </div>
        </div>
      </div>
    </section>
  )
}

function MethodDot({
  index,
  activeIndex,
  label,
}: {
  index: number
  activeIndex: MotionValue<number>
  label: string
}) {
  const proximity = useTransform(activeIndex, (v) => Math.max(0, 1 - Math.abs(v - index)))
  const opacity = useTransform(proximity, [0, 1], [0.3, 1])
  const width = useTransform(proximity, [0, 1], [20, 56])

  return (
    <div className="flex flex-col gap-2">
      <motion.div style={{ opacity, width }} className="h-[3px] rounded-full bg-[var(--velova-blue-deep)]" />
      <motion.span style={{ opacity }} className="velova-mono-label text-[9.5px] whitespace-nowrap">
        {label}
      </motion.span>
    </div>
  )
}

function MethodCard({
  index,
  total,
  activeIndex,
  step,
  narrow,
}: {
  index: number
  total: number
  activeIndex: MotionValue<number>
  narrow: boolean
  step: {
    num: string
    title: string
    tagline: string
    body: string
    footer: string
    gradient: string
    glow: string
  }
}) {
  const relPos = useTransform(activeIndex, (v) => index - v)
  // Same choreography either way; the phone gets a shallower fan-out and a
  // shorter exit so the deep cards stay inside a ~346px column.
  const stops = [-total, -1, 0, 1, 2, 3]
  const y = useTransform(relPos, stops, narrow ? [-250, -165, 0, 12, 24, 34] : [-620, -380, 0, 20, 40, 56])
  const x = useTransform(relPos, stops, narrow ? [-90, -55, 0, 14, 28, 40] : [-300, -168, 0, 38, 74, 106])
  const rotate = useTransform(
    relPos,
    stops,
    narrow ? [-13, -9, 0, -2, -3.8, -5.4] : [-18, -12, 0, -2.8, -5.4, -7.8],
  )
  const scale = useTransform(
    relPos,
    stops,
    narrow ? [0.86, 0.9, 1, 0.97, 0.94, 0.9] : [0.82, 0.88, 1, 0.96, 0.92, 0.88],
  )
  const opacity = useTransform(
    relPos,
    narrow ? [-0.62, -0.32, 0, total + 1] : [-1.15, -1, 0, total + 1],
    narrow ? [0, 0.3, 1, 1] : [0, 0.38, 1, 1],
  )
  const filter = useTransform(relPos, (v) => {
    const c = Math.max(0, Math.min(3, v))
    return `blur(${(c * 0.7).toFixed(2)}px)`
  })

  return (
    <motion.div
      style={{
        y,
        x,
        rotate,
        scale,
        opacity,
        filter,
        zIndex: total - index,
        background: step.gradient,
        boxShadow: `0 38px 80px -28px ${step.glow}, 0 14px 32px -10px rgba(0,0,0,0.45)`,
      }}
      className="absolute left-0 top-0 w-[88%] sm:w-[86%] md:w-[82%] h-[268px] sm:h-[344px] md:h-[480px] rounded-[20px] md:rounded-[28px] p-5 sm:p-7 md:p-10 flex flex-col text-white overflow-hidden will-change-transform"
    >
      <div
        aria-hidden
        className="absolute inset-0 rounded-[inherit] pointer-events-none"
        style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.16), inset 0 0 0 1px rgba(255,255,255,0.07)" }}
      />
      <div
        aria-hidden
        className="absolute -top-28 -right-20 w-[460px] h-[460px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(255,255,255,0.26) 0%, transparent 60%)" }}
      />
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none mix-blend-overlay opacity-25"
        style={{
          backgroundImage:
            "radial-gradient(circle at 28% 18%, rgba(255,255,255,0.14), transparent 38%), radial-gradient(circle at 72% 82%, rgba(0,0,0,0.28), transparent 50%)",
        }}
      />

      <div className="relative">
        <h3 className="velova-display text-[30px] sm:text-[38px] md:text-[52px] lg:text-[60px] leading-[0.94] tracking-[-0.015em]">
          {step.title}
        </h3>
        <p className="mt-2 md:mt-4 velova-italic text-[14px] sm:text-[16px] md:text-[19px] leading-[1.3] md:leading-[1.35] opacity-90 max-w-[440px]">
          {step.tagline}
        </p>
      </div>

      <div className="relative mt-auto">
        <p className="text-[11.5px] sm:text-[13px] md:text-[14.5px] leading-[1.45] md:leading-[1.55] opacity-85 max-w-[480px]">
          {step.body}
        </p>
        <div className="mt-4 pt-4 border-t border-white/15">
          <span className="velova-mono-label opacity-80">{step.footer}</span>
        </div>
      </div>
    </motion.div>
  )
}
